import * as React from 'react';
import propz from 'propz';
import Moment from 'moment';
import { SchoolInvite } from 'Src/models/invite';
import { AppUser } from 'Src/views/App/App';
import { getTournamentLocation } from 'Src/helpers/invite/invite';
import { isIndividualTournament } from 'Src/helpers/tournament/tournament';
import { AddTeamsForm } from '../AddTeamsForm/AddTeamsForm';
import { TournamentInviteBookingModalContent } from '../TournamentInviteBookingModalContent';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { TournamentInviteEventSelection } from '../TournamentInviteEventSelection';

interface Props {
  invite: SchoolInvite;
  user: AppUser;
  onCancel: () => void;
  onAccept: (data?) => void;
  events: any[];
  selectedEventIds: string[];
  eventSelectionError: string;
  onEventSelectionChange: (eventIds: string[]) => void;
}

export function AcceptTournamentInviteWithAutoEnrollmentTeams(props: Props) {
  const { onCancel, onAccept, invite, user, events, selectedEventIds, eventSelectionError, onEventSelectionChange } = props;
  const { activeSchoolId } = user;
  const { tournament, inviterSchool } = invite;

  if (!tournament) {
    return null;
  }

  const tournamentLocation = getTournamentLocation(invite, activeSchoolId);
  const tournamentSportName = propz.get<string>(tournament, ['sport', 'name'], '');
  const tournamentName = propz.get<string>(tournament, ['name'], '');
  const tournamentStartTime = propz.get<string>(tournament, ['startTime']);
  const tournamentStartTimeFormatted = tournamentStartTime
    ? Moment(tournament.startTime).format('DD.MM.YYYY / HH:mm')
    : '';

  const inviterSchoolName = propz.get<string>(inviterSchool, ['name'], '');
  const isIndividual = isIndividualTournament(tournament);
  const autoEnrollmentTeamsSettings = Array.isArray(tournament.autoEnrollmentTeamsSettings)
    ? tournament.autoEnrollmentTeamsSettings
    : [];

  let isAllGenderAndAgeTeamPlacesBooked = false;

  if (isIndividual) {
    const [autoEnrollmentTeamsSetting] = autoEnrollmentTeamsSettings;
    const maximumNumberTeams = Number(propz.get(autoEnrollmentTeamsSetting, ['maximumNumberTeams'], 0));
    const currentNumberTeams = Number(propz.get(autoEnrollmentTeamsSetting, ['currentNumberTeams'], 0));

    isAllGenderAndAgeTeamPlacesBooked = maximumNumberTeams === currentNumberTeams && maximumNumberTeams !== 0;
  } else {
    isAllGenderAndAgeTeamPlacesBooked = autoEnrollmentTeamsSettings.every(autoEnrollmentTeamsSetting => {
      const maximumNumberTeams = Number(propz.get(autoEnrollmentTeamsSetting, ['maximumNumberTeams'], 0));
      const currentNumberTeams = Number(propz.get(autoEnrollmentTeamsSetting, ['currentNumberTeams'], 0));

      return maximumNumberTeams === currentNumberTeams && maximumNumberTeams !== 0;
    });
  }

  const isAutoEnrollmentToWaitingList = propz.get<boolean>(tournament, ['isAutoEnrollmentToWaitingList'], false);
  const isWaitingListBooking = isAllGenderAndAgeTeamPlacesBooked || isAutoEnrollmentToWaitingList;
  const isShowAddTeamsForm = !isAllGenderAndAgeTeamPlacesBooked && !isIndividual && !isAutoEnrollmentToWaitingList;
  const maxPlayersPerTeam = Number(tournament.maxPlayersPerTeam || 0);
  const isShowIndividualStudentsForm = isIndividual && !isWaitingListBooking && maxPlayersPerTeam > 0;
  const buttonText = isWaitingListBooking ? 'Join waiting list' : 'Book spaces';

  return (
    <TournamentInviteBookingModalContent
      title={isWaitingListBooking ? 'Join waiting list' : 'Book tournament spaces'}
      description={
        isWaitingListBooking
          ? 'This tournament is currently full. If you continue, your school will be placed on the waiting list.'
          : isIndividual
          ? 'Your school will be registered as a participant in this tournament.'
          : 'Choose how many teams your school plans to bring, then confirm your booking.'
      }
      details={[
        { label: 'Tournament', value: tournamentName },
        { label: 'Organiser', value: inviterSchoolName },
        { label: 'Sport', value: tournamentSportName },
        { label: 'Start time', value: tournamentStartTimeFormatted },
        { label: 'Venue', value: tournamentLocation }
      ]}
      onClose={onCancel}
      isWaitingList={isWaitingListBooking}
      actions={
        isWaitingListBooking || (isIndividual && !isShowIndividualStudentsForm) ? (
          <React.Fragment>
            <button type="button" className="tournamentBookingModal__button" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="tournamentBookingModal__button isPrimary"
              onClick={() => onAccept()}
            >
              {buttonText}
            </button>
          </React.Fragment>
        ) : null
      }
    >
      <TournamentInviteEventSelection
        events={events}
        selectedEventIds={selectedEventIds}
        error={eventSelectionError}
        onChange={onEventSelectionChange}
      />
      {isShowAddTeamsForm ? (
        <section className="tournamentBookingModal__teamSelection">
          <h3>Team selection</h3>
          <p>Enter how many teams your school plans to bring for each available category.</p>
          <AddTeamsForm tournament={tournament} onSubmit={onAccept} onCancel={onCancel} />
        </section>
      ) : isShowIndividualStudentsForm ? (
        <section className="tournamentBookingModal__teamSelection">
          <h3>Team size</h3>
          <p>Enter how many students your school plans to bring.</p>
          <IndividualTeamStudentsForm
            maxPlayersPerTeam={maxPlayersPerTeam}
            onSubmit={onAccept}
            onCancel={onCancel}
          />
        </section>
      ) : null}
    </TournamentInviteBookingModalContent>
  );
}

function IndividualTeamStudentsForm(props: { maxPlayersPerTeam: number; onCancel: () => void; onSubmit: (data: any) => void }) {
  const { maxPlayersPerTeam, onCancel, onSubmit } = props;

  return (
    <Formik
      initialValues={{ numberOfStudents: '' }}
      validate={({ numberOfStudents }) => {
        const count = Number(numberOfStudents);
        if (numberOfStudents === '' || !Number.isInteger(count) || count < 1) {
          return { numberOfStudents: 'Please enter the number of students' };
        }
        if (count > maxPlayersPerTeam) {
          return { numberOfStudents: `Max ${maxPlayersPerTeam} students` };
        }
        return {};
      }}
      onSubmit={onSubmit}
    >
      <Form className="tournamentTeamsForm">
        <div className="tournamentTeamsForm__fields">
          <label className="tournamentTeamsForm__field">
            <span>
              Students in the team <small>Maximum {maxPlayersPerTeam}</small>
            </span>
            <Field type="number" name="numberOfStudents" min={1} max={maxPlayersPerTeam} aria-required="true" />
            <ErrorMessage name="numberOfStudents" component="span" className="tournamentTeamsForm__fieldError" />
          </label>
        </div>
        <div className="tournamentTeamsForm__actions">
          <button type="button" className="tournamentTeamsForm__button" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="tournamentTeamsForm__button isPrimary">
            Confirm booking
          </button>
        </div>
      </Form>
    </Formik>
  );
}

